/**
 * Age in, birth date out (BR-12).
 *
 * The database stores `dob_estimate`, a date. A partner standing at a haat
 * stores nothing of the sort in their head: a goat is "about ten months", and
 * the seller does not know its birthday either. Asking for a date would mean
 * the partner does the subtraction — badly, in a shed, on a phone — and a
 * wrong date is worse than an honest estimate because it looks exact.
 *
 * So the form asks for months and this file does the arithmetic. The column
 * stays a date, which is what every reader of it already expects: growth
 * curves, the qurbani weight trajectory and the herd list all subtract from it
 * and would each need changing if age were stored as a number.
 *
 * The round trip is deliberately lossy in one direction only. Months → date
 * picks the day that many months back; date → months floors. Ten months in
 * reads back as ten months out on the day it was entered, and as eleven a
 * month later, which is the point of storing a date rather than a number.
 */

import dayjs from 'dayjs';

import { todayInDhaka, type IsoDate } from '../../lib/date.js';

/** A goat that lives 15 years is remarkable; 40 years of months is beyond any. */
export const MAX_AGE_MONTHS = 480;

/** Digits only, and not a leading-zero oddity like "007". */
const WHOLE_NUMBER = /^(0|[1-9]\d*)$/;

/**
 * The birth date an animal of this many months would have, counted back from
 * today in Dhaka.
 *
 * Returns `null` for anything that is not a usable age, so a caller can leave
 * `dobEstimate` out of the payload entirely rather than send a guess. An empty
 * box means "nobody knows", which is a real and common answer — it must not
 * become a birthday of today.
 *
 * `dayjs.subtract(n, 'month')` clamps rather than overflowing: 31 March less
 * one month is 28 February, not 3 March. That is the behaviour we want. An
 * animal's estimated birthday landing on the 28th instead of the 31st changes
 * nothing a partner can perceive, whereas a date that silently jumped into the
 * following month would make the age read one lower than what was typed.
 */
export function dobFromAgeMonths(months: string | number, today: IsoDate = todayInDhaka()): IsoDate | null {
  const text = typeof months === 'number' ? String(months) : months.trim();
  if (!WHOLE_NUMBER.test(text)) return null;

  const value = Number(text);
  if (value > MAX_AGE_MONTHS) return null;

  return dayjs(today, 'YYYY-MM-DD').subtract(value, 'month').format('YYYY-MM-DD');
}

/**
 * Whole months an animal has lived, from the `ageDays` the API sends.
 *
 * The server sends days because days is what it can compute without a calendar
 * library; partners speak in months. This is the one place that converts, so
 * the herd list and the profile can never disagree about an animal's age.
 *
 * Not `days / 30`. A 365-day-old animal is twelve months old, and thirty-day
 * months make it twelve-and-a-bit — which rounds to 12 today and 13 in a
 * fortnight, so a partner watching a list would see an animal age a month
 * early. Counting calendar months back from today is what "eleven months old"
 * actually means.
 */
export function ageMonthsFromDays(ageDays: number | null | undefined, today: IsoDate = todayInDhaka()): number | null {
  if (ageDays === null || ageDays === undefined || ageDays < 0) return null;

  const born = dayjs(today, 'YYYY-MM-DD').subtract(ageDays, 'day');
  return dayjs(today, 'YYYY-MM-DD').diff(born, 'month');
}

/**
 * The age a partner typed, read back for an edit form.
 *
 * Takes the stored `dobEstimate` rather than `ageDays` so an edit screen shows
 * the same number that was entered, not one derived through two conversions.
 */
export function ageMonthsFromDob(dob: IsoDate | null, today: IsoDate = todayInDhaka()): number | null {
  if (dob === null) return null;

  const months = dayjs(today, 'YYYY-MM-DD').diff(dayjs(dob, 'YYYY-MM-DD'), 'month');
  return months < 0 ? null : months;
}
